// The rpg layer's share of the page (phase D1, gates D1.1 and D1.2): the market lab's handle (`__rpg.rpg`: state,
// events, the economy's actions, enemies to defeat, the content check), its invariants, its planted defects
// (`classUnknownStat`, `dropMissingItem`, `sellAtBuyPrice`, by `?faults=` or `__rpg.faults`) and its screens on the UI
// (the inventory and skills from the pause menu; the shop, forge and pot from the world's stations and NPCs).
import { finite, type Invariant } from "@rpg/harness";
import { SHOP_FAULTS } from "@rpg/items";
import type { Runtime } from "@rpg/runtime";
import type { ScreenRender } from "@rpg/ui";
import type { SpikeContent } from "../content";
import type { FaultGroup } from "../faults";
import { HERO_ID, type Fight } from "../enemies/fight";
import type { Things } from "../things/module";
import type { ValleyRig } from "../valley/scene";
import { RPG_FAULTS, readRpgContent, type RpgFaultName } from "./content";
import { MARKET_ARENA } from "./lab";
import type { Rpg, RpgOpen } from "./module";
import { rpgScreens, type StationParams } from "./screens";
import type { Conditions } from "@rpg/story";
import type { RpgView } from "./view";

export { marketLab, type MarketLabMount } from "./lab";
export { mountRpgView, type RpgView } from "./view";
export { RPG_FAULTS } from "./content";
export type { Rpg } from "./module";

export const RPG_FAULT_NAMES = Object.keys(RPG_FAULTS) as RpgFaultName[];

/** Switch the named planted defects on (the sim mount calls it with `?faults=` before the lab reads its content). */
export function setRpgFaults(names: readonly string[]): void {
  for (const n of names) if ((RPG_FAULT_NAMES as string[]).includes(n)) RPG_FAULTS[n as RpgFaultName] = true;
  SHOP_FAULTS.sellAtBuyPrice = RPG_FAULTS.sellAtBuyPrice;
}

/** The rpg layer's planted defects as a group for `__rpg.faults`. */
export function rpgFaultGroup(search = typeof location !== "undefined" ? location.search : ""): FaultGroup {
  setRpgFaults(new URLSearchParams(search).get("faults")?.split(",") ?? []);
  return {
    names: RPG_FAULT_NAMES,
    get: (n) => RPG_FAULTS[n as RpgFaultName] === true,
    set: (n, on) => {
      RPG_FAULTS[n as RpgFaultName] = on;
      SHOP_FAULTS.sellAtBuyPrice = RPG_FAULTS.sellAtBuyPrice;
    },
  };
}

/** The invariants after every step: the inventory, the equipment and the progress sound; the gold and the hero's
 *  numbers finite, the gold never below 0. */
export function rpgInvariants(rpg: () => Rpg): Invariant[] {
  return [
    () => {
      const r = rpg();
      const s = r.state();
      const issues = [...r.inventory.check(s.bag), ...r.equipment.check(s.gear, s.bag), ...r.progression.check(s.progress)];
      return issues.length ? `rpg: ${issues.join("; ")}` : null;
    },
    () => (rpg().state().wallet.balance < 0 ? `rpg: gold ${rpg().state().wallet.balance}` : null),
    finite("rpg", () => {
      const r = rpg();
      const st = r.heroStats();
      return [r.state().wallet.balance, r.state().progress.xp, ...(st ? Object.values(st.base) : [])];
    }),
  ];
}

/** `__rpg.rpg`: the market lab's rpg layer for the specs and the shots. */
export function rpgHandle(d: { rpg: () => Rpg; fight: () => Fight; things: () => Things; rig: () => ValleyRig; runtime: Runtime; conditions: Conditions; standing: (faction: string, value: number) => void; content: SpikeContent; view: () => RpgView | null; ui: () => RpgUi | null; teleport?: (x: number, z: number, yaw: number) => void; arena?: { x: number; z: number } }) {
  const summary = () => {
    const r = d.rpg();
    const s = r.state();
    const st = r.heroStats();
    const set = r.abilities();
    const stat = (id: string): number | null => (st && set.attributes.has(id) ? set.value(st, id) : null);
    return {
      ...r.progress(),
      gold: s.wallet.balance,
      bag: s.bag.stacks.map((x) => ({ ...x })),
      gear: { ...s.gear.slots },
      levels: { ...s.gear.levels },
      glyphs: r.glyphs(),
      scarfLinks: r.scarfLinks(),
      heroScale: r.heroScale(),
      stats: { hp: stat("hp"), hpMax: stat("hpMax"), attack: stat("attack"), defence: stat("defence"), moveScale: stat("moveScale"), critChance: stat("critChance") },
      tags: st ? set.tags(st) : [],
      weapon: r.weapon(),
      wielding: d.fight().view(HERO_ID)?.weapon ?? null,
      nodes: structuredClone(s.progress.nodes),
    };
  };
  return {
    state: summary,
    full: () => structuredClone(d.rpg().state()),
    events: (n = 200) => d.rpg().events.slice(-n),
    count: (item: string) => d.rpg().inventory.count(d.rpg().state().bag, item),
    /** Spawn an enemy to defeat (in the square's open north by default). Returns its id. */
    spawn: (kind: string, x = (d.arena ?? MARKET_ARENA).x, z = (d.arena ?? MARKET_ARENA).z, alert = false) => d.fight().spawn(kind, x, z, { alert }),
    /** Defeat an enemy at the next step, as the hero's blow would. */
    defeat: (id: number) => d.fight().defeat(id, [0, 0, -1]),
    enemies: () => d.fight().state().enemies.map((e) => ({ id: e.id, kind: e.kind, alive: e.alive, pos: [...e.pos] })),
    heroIntent: (kind: "attack" | "skill" | "big" | "dodge" | "block" | "blockRelease") => void (d.fight().heroIntent = { kind }),
    fighter: () => d.fight().view(HERO_ID),
    /** Any fighter's combat view (an enemy's tags: a stun landed). */
    fighterOf: (id: number) => d.fight().view(id),
    /** The hero's active effects (id, stacks, seconds left): the level, the bought nodes, the equipment, statuses. */
    heroEffects: () => (d.rpg().heroStats()?.effects ?? []).map((e) => ({ id: e.id, stacks: e.stacks, left: e.left })),
    /** Set the hero's health (a status check starts hurt). */
    setHp: (hp: number) => {
      const st = d.rpg().heroStats();
      if (st) d.rpg().abilities().setBase(st, "hp", hp);
    },
    grantXp: (amount: number, source = "test") => d.rpg().grantXp(amount, source),
    addItem: (item: string, count: number) => d.rpg().addItem(item, count, "test"),
    addGold: (amount: number) => d.rpg().addGold(amount, "test"),
    buy: (shop: string, item: string, count = 1) => d.rpg().buy(shop, item, count),
    sell: (shop: string, item: string, count = 1) => d.rpg().sell(shop, item, count),
    craft: (recipe: string, station?: string) => d.rpg().craft(recipe, station),
    equip: (slot: string, item: string) => d.rpg().equip(slot, item),
    unequip: (slot: string) => d.rpg().unequip(slot),
    use: (item: string) => d.rpg().use(item),
    learn: (tree: string, node: string) => d.rpg().learn(tree, node),
    discount: (shop: string) => d.rpg().discount(shop),
    prices: (shop: string, item: string) => ({ buy: d.rpg().shops.buyPrice(shop, item, d.rpg().discount(shop)), sell: d.rpg().shops.sellPrice(shop, item, d.rpg().discount(shop)) }),
    /** Every priced item at every shop, at no discount and at the shop's best: the lines where selling is not below
     *  buying (empty when the economy's rule holds; `sellAtBuyPrice` plants a break). */
    sellBelowBuy: () => {
      const r = d.rpg();
      const out: string[] = [];
      for (const [id, info] of r.content.shops)
        for (const it of r.content.items.list())
          for (const off of [0, ...info.discounts.map((x) => x.off)])
            if (it.price !== undefined && it.price > 0 && !(r.shops.sellPrice(id, it.id, off) < r.shops.buyPrice(id, it.id, off))) out.push(`${id}:${it.id}@${off}`);
      return out;
    },
    /** Set a faction's standing (the story's, or the lab's own); its tier's perks follow (the shops' discount). */
    setStanding: (faction: string, value: number) => d.standing(faction, value),
    fact: (name: string) => d.conditions.fact(name),
    /** The rpg content's validation with the planted defects as switched now (empty: clean). */
    check: () => readRpgContent(d.content, RPG_FAULTS).issues,
    /** The class templates as loaded (gate D1.1: loaded and validated; none played). */
    classes: () => d.rpg().content.classes.map((c) => c.id),
    /** Where a station or NPC stands, and stand the hero in front of it facing it. */
    place: (id: string) => {
      const m = d.rpg().places.find((p) => p.id === id);
      return m ? [m.pos[0]!, d.rig().terrainAt(m.pos[0]!, m.pos[1]!), m.pos[1]!] : null;
    },
    /** The scene's stations and NPC stand-ins (the market's, or the valley's stations from the layout). */
    stations: () => d.rpg().places.map((p) => ({ id: p.id, kind: p.kind, station: p.station ?? null, pos: [p.pos[0]!, p.pos[1]!] as [number, number] })),
    /** Stand the hero `distance` m in front of a station (on its +z side, facing it), or `warp` it first in a streamed
     *  world (`teleportHero` loads the chunks). */
    faceStation: (id: string, distance = 1.4) => {
      const m = d.rpg().places.find((p) => p.id === id);
      if (!m) throw new Error(`no station "${id}"`);
      const x = m.pos[0]!;
      const z = m.pos[1]! + distance;
      if (d.teleport) d.teleport(x, z, Math.PI);
      else d.rig().warpHero(x, d.rig().terrainAt(x, z), z, Math.PI);
    },
    warp: (x: number, z: number, yaw = Math.PI) => (d.teleport ? d.teleport(x, z, yaw) : d.rig().warpHero(x, d.rig().terrainAt(x, z), z, yaw)),
    press: () => void (d.things().press = true),
    choice: () => d.things().choice().id,
    view: () => d.view()?.report() ?? null,
    /** Open one of the rpg screens (the inventory and skills as from the pause menu; a station as from the world). */
    open: (screen: string, params?: StationParams) => d.ui()?.open(screen, params) ?? false,
  };
}

/** The rpg screens mounted on the UI. */
export interface RpgUi {
  /** Open a screen: the inventory or skills over the pause menu, a station's from the world. */
  open(screen: string, params?: StationParams): boolean;
}

/** Mount the rpg layer's screens on the game's UI: added to its stack's screens, the inventory and skills in the pause
 *  menu, and a station or NPC used in the world opening its screen over the pause screen (back resumes play). */
export function mountRpgUi(d: {
  runtime: Runtime;
  content: SpikeContent;
  rpg: () => Rpg | null;
  addScreens(more: Record<string, ScreenRender>, extras: { label: string; action: string; screen: string }[]): void;
  push(id: string, params?: unknown): void;
  top(): string | null;
  flow: { pause(): void; resume(): void; state(): string | null };
}): RpgUi {
  d.addScreens(rpgScreens({ rpg: d.rpg, content: d.content, resume: () => d.flow.resume() }), [
    { label: "ui.inventory", action: "inventory", screen: "inventory" },
    { label: "ui.skills", action: "skills", screen: "skills" },
  ]);
  const open = (screen: string, params?: StationParams): boolean => {
    if (!d.rpg()) return false;
    if (d.flow.state() === "play") d.flow.pause();
    if (d.flow.state() !== "pause") return false;
    d.push(screen, params);
    return true;
  };
  // a station or NPC fired inside a step: open its screen after the step, outside the sim
  d.runtime.on(
    "late",
    () => {
      const r = d.rpg();
      const o: RpgOpen | null = r ? r.takeOpen() : null;
      if (o) open(o.kind, { id: o.id, fromWorld: true });
    },
    { name: "rpg: open a station's screen" },
  );
  return { open };
}
